import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Download, Package } from "lucide-react";

const FULFILL_STATUSES = ["Confirmed", "Preparing", "Ready", "Completed"];
const PAY_LABEL = { pending_approval: "Pending Approval", partially_paid: "Partially Paid", paid: "Paid", rejected: "Rejected", unpaid: "Unpaid" };

export default function BatchPlanner() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [ids, setIds] = useState([]);
  const [sortKey, setSortKey] = useState("order_number");

  useEffect(() => {
    const stored = JSON.parse(sessionStorage.getItem("drelik_plan_ids") || "[]");
    if (!stored.length) { navigate("/dashboard/orders"); return; }
    setIds(stored);
    api.post("/store/orders/batch/plan", { order_ids: stored }).then((r) => setPlan(r.data));
  }, [navigate]);

  const reload = () => api.post("/store/orders/batch/plan", { order_ids: ids }).then((r) => setPlan(r.data));

  const applyStatus = async (status) => {
    await api.post("/store/orders/batch/status", { order_ids: ids, status });
    toast.success(`${ids.length} orders → ${status}`);
    reload();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("drelik_token");
    const res = await fetch(`${API}/store/orders/batch/export`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ order_ids: ids }),
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "drelik-orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!plan) return null;
  const s = plan.summary;
  const rows = [...plan.rows].sort((a, b) => String(a[sortKey]).localeCompare(String(b[sortKey]), undefined, { numeric: true }));

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/dashboard/orders")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="planner-back"><ArrowLeft className="h-4 w-4" /> Back to orders</button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Batch Order Planner</h1><p className="text-muted-foreground mt-1">Plan and fulfill {s.order_count} selected orders.</p></div>
        <Button variant="outline" className="rounded-lg" onClick={exportCsv} data-testid="planner-export-csv"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="planner-summary">
        {[["Orders", s.order_count], ["Customers", s.unique_customers], ["Combined Sales", money(s.combined_sales)], ["Confirmed", money(s.confirmed_payments)], ["Pending Approval", money(s.pending_approvals)], ["Outstanding", money(s.outstanding_balance)]].map(([k, v]) => (
          <Card key={k} className="p-4 rounded-xl border-border"><p className="text-xs text-muted-foreground">{k}</p><p className="text-lg font-bold mt-1">{v}</p></Card>
        ))}
      </div>

      <Card className="rounded-xl border-border p-5">
        <div className="flex items-center gap-2 mb-3"><Package className="h-4 w-4 text-muted-foreground" /><h3 className="font-semibold">Product totals to prepare</h3></div>
        <div className="flex flex-wrap gap-2" data-testid="product-totals">
          {plan.product_totals.map((p) => (
            <div key={p.name} className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"><span className="font-medium">{p.name}</span><Badge variant="secondary" className="rounded-full border-0">×{p.quantity}</Badge></div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Batch actions:</span>
        {FULFILL_STATUSES.map((st) => <Button key={st} size="sm" variant="outline" className="rounded-lg" onClick={() => applyStatus(st)} data-testid={`batch-status-${st}`}>{st}</Button>)}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort</span>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger className="h-9 w-40" data-testid="planner-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="order_number">Order #</SelectItem>
              <SelectItem value="customer_name">Customer</SelectItem>
              <SelectItem value="payment_status">Payment Status</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-xl border-border overflow-x-auto" data-testid="planner-table">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead>Customer</TableHead><TableHead>Order #</TableHead><TableHead>Products</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Claimed</TableHead>
              <TableHead className="text-right">Confirmed</TableHead><TableHead className="text-right">Balance</TableHead>
              <TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Proof</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`planner-row-${r.id}`}>
                <TableCell className="font-medium">{r.customer_name}</TableCell>
                <TableCell>{r.order_number}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[220px]">{r.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</TableCell>
                <TableCell className="text-right">{money(r.total)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{r.claimed_amount != null ? money(r.claimed_amount) : "—"}</TableCell>
                <TableCell className="text-right">{money(r.confirmed_amount)}</TableCell>
                <TableCell className="text-right">{money(r.remaining_balance)}</TableCell>
                <TableCell><Badge variant="secondary" className="rounded-full border-0 text-xs">{PAY_LABEL[r.payment_status] || r.payment_status}</Badge></TableCell>
                <TableCell className="text-sm">{r.status}</TableCell>
                <TableCell>{r.proof_available ? <span className="text-emerald-600 text-xs font-medium">Yes</span> : <span className="text-muted-foreground text-xs">No</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
